/**
 * The dashboard's numbers, derived.
 *
 * Everything this module returns is a function of two inputs the learner and
 * the content authors actually control — the content catalogue and the progress
 * store's `stages` — and of nothing else. That is the whole point: what stood
 * here before was a literal array asserting `4 of 4` and `2 of 5`, numbers that
 * had never met a piece of content or a completion. A frontliner reading them
 * was reading fiction.
 *
 * `DashboardScreen` renders what comes out of here and computes nothing, which
 * is what makes both halves testable: these functions are pure and take their
 * inputs by argument, so a case like "guided finished, unguided pending" is a
 * two-line fixture rather than a driven UI.
 *
 * ## The two sources, and which one answers what
 *
 * - CONTENT (`content-catalogue.ts`) answers *what exists*: how many lessons a
 *   module has, and whether a lesson has an unguided stage at all.
 * - THE STORE (`@/player/lesson-progress`) answers *what the learner did*, and
 *   completion is decided by its own `isLessonComplete`, never re-derived here.
 *   The store's `StageRecord.requiresUnguided` records what was true when the
 *   learner finished; conflating it with what content says today is what lets a
 *   content edit retroactively un-complete somebody's lesson.
 *
 * ## Registry drift is expected, not exceptional
 *
 * `MODULE_REGISTRY` is hand-maintained presentation metadata and it has already
 * drifted from the JSON in two places:
 *
 * - `soh-lesson-4`, `soh-lesson-5` — the JSON calls them `lesson-4`, `lesson-5`
 * - `stocktake-lesson-4` — the JSON calls it `lesson-4`
 *
 * So content leads and the registry decorates. A lesson with no registry entry
 * still counts toward its module's total (it exists; the learner can open it)
 * and simply carries no metadata — it is never guessed into the Required
 * Training list, because a missing entry makes no claim and "required" needs
 * two: the registry's `SubModuleMeta.required` and the role's own
 * `required: true` in roles.json. A module with no registry entry still
 * renders, with an empty icon/colour name the screen resolves to its fallback.
 *
 * A real duplicate lives in the content too: `lesson-4` is a sub-module id in
 * BOTH `soh-adjustment` and `stocktake-essentials`, and the progress store is
 * keyed by sub-module id alone — so finishing "Lesson 4" counts in both modules
 * here. That is the content's ambiguity, not this module's to paper over:
 * `findModuleIdFor` refuses to report such a completion to the Sheet for the
 * same reason. Renaming one of them fixes it everywhere at once.
 */

import {
  MODULE_REGISTRY,
  type ModuleRegistryEntry,
  type SubModuleMeta,
} from '@/content/module-registry'
import rolesManifest from '@/content/roles.json'
import { isLessonComplete, type StageRecord } from '@/player/lesson-progress'
import type { CatalogueLesson, CatalogueModule, ContentCatalogue } from './content-catalogue'

interface RoleCurriculumEntry {
  moduleId: string
  subModuleId: string
  required: boolean
}

/**
 * `"moduleId::subModuleId"` keys from a role's curriculum, as a set.
 *
 * Returns null when the role has no curriculum entry in roles.json — callers
 * treat null as "show all lessons" (unknown or portal roles).
 *
 * `requiredOnly` is the distinction that matters: a role's curriculum holds
 * BOTH required and optional lessons, and they are not the same state as a
 * lesson that is absent from the curriculum altogether.
 *
 * - Membership (`requiredOnly: false`) decides what is COUNTED and REACHABLE —
 *   module cards, module totals, overall progress, the module page's list.
 * - `required: true` decides only what the Required Training list DEMANDS.
 *
 * RA_SK has 2 required and 12 optional lessons: it should see fourteen lessons
 * across its module cards and be asked for two. Counting only the required two
 * is what left its Ordering and Receiving cards empty.
 */
function curriculumKeySet(
  roleId: string | null,
  requiredOnly: boolean,
): ReadonlySet<string> | null {
  if (!roleId) return null
  const roles = rolesManifest.roles as Record<string, { curriculum: RoleCurriculumEntry[] }>
  const role = roles[roleId]
  if (!role) return null
  const entries = requiredOnly ? role.curriculum.filter((e) => e.required) : role.curriculum
  return new Set(entries.map((e) => `${e.moduleId}::${e.subModuleId}`))
}

/** The card's bottom-pill vocabulary. Unchanged — only what lands in it is new. */
export type ModuleStatus = 'done' | 'in-progress' | 'pending'

/**
 * How far into a lesson the learner is.
 *
 * `started` is the state this screen used to have no word for: guided finished,
 * unguided still to do, rendered as a flat 0% with nothing to show the learner
 * their work registered.
 */
export type LessonState = 'not-started' | 'started' | 'complete'

export interface ModuleCardModel {
  /** The `/module/:id` route param — the registry's `navId`, else the JSON id. */
  readonly id: string
  readonly title: string
  readonly subtitle: string
  /** A key into the screen's `ICON_MAP`; `''` when no registry entry exists. */
  readonly icon: string
  /** A key into the screen's `ACCENT_BG`; `''` when no registry entry exists. */
  readonly accentColour: string
  /** Lessons in this module that are FULLY complete. */
  readonly progress: number
  /** Lessons in this module, full stop. */
  readonly total: number
  /** Lessons touched but not finished — the partial-progress signal. */
  readonly started: number
  /** Stage-weighted percentage: see `percentOf`. */
  readonly percent: number
  readonly status: ModuleStatus
  /** Placeholder module — shown as Pending, not navigable. */
  readonly comingSoon: boolean
}

export interface RequiredLessonModel {
  readonly id: string
  readonly title: string
  /** The module's authored title, e.g. `"Ordering Essentials"`. */
  readonly module: string
  /** The lesson's position in its module, e.g. `"Lesson 1"`. */
  readonly submodule: string
  readonly estimatedMinutes: number
  readonly priority: 'high' | 'medium'
  readonly dueLabel: string | null
  readonly tags: readonly string[]
  readonly icon: string
  readonly accentColour: string
  readonly state: LessonState
  /** Module is not yet released — show as Pending, block interaction. */
  readonly comingSoon: boolean
}

export interface OverallProgress {
  readonly completeLessons: number
  readonly totalLessons: number
  /** Stage-weighted, so a finished guided stage moves it off zero. */
  readonly percent: number
}

export interface DashboardModel {
  readonly modules: readonly ModuleCardModel[]
  readonly requiredLessons: readonly RequiredLessonModel[]
  readonly overall: OverallProgress
}

export type ModuleRegistry = Readonly<Record<string, ModuleRegistryEntry>>

export interface DeriveOptions {
  /** Injectable for tests. Defaults to the real `MODULE_REGISTRY`. */
  readonly registry?: ModuleRegistry
  /** Filters each module's lessons to the required entries in roles.json for
   *  this role. Pass `getSessionIdentity()?.roleId` from DashboardScreen. */
  readonly roleId?: string | null
}

const PERCENT = 100
/** A lesson with an unguided stage takes two stages to finish; others take one. */
const STAGES_WITH_UNGUIDED = 2
const STAGES_GUIDED_ONLY = 1
/** `"Module 01 • Ordering"` → `"Ordering"`. */
const MODULE_TAG_PREFIX = /^.*•\s*/
/** `"Lesson 1: Adding items to your cart"` → `"Adding items to your cart"`. */
const LESSON_TITLE_PREFIX = /^Lesson \d+[a-z]?: /

/**
 * THE ROLE FILTER — wired to roles.json.
 *
 * Every number on the dashboard — module totals, Required Training list,
 * overall progress — is counted from what this returns. A filter applied here
 * is applied everywhere; there is no second place that can disagree with it.
 *
 * It gates on curriculum MEMBERSHIP, not on `required`. A role's curriculum
 * holds both required and optional lessons, and an optional lesson is one the
 * role is offered — not one hidden from it. Gating here on `required` is what
 * made RA_SK's Ordering and Receiving cards read `0/0` and render as "Coming
 * Soon": all fourteen of that role's ordering/receiving lessons are
 * `required: false`, so the module had no lessons to count even though
 * `ModuleLessonsScreen` listed every one of them. The card and the module page
 * now count the same lessons, which is the only way they cannot disagree.
 *
 * `required` still decides ONE thing, and only in `deriveDashboard`'s third
 * pass: whether a lesson appears in the Required Training list. See
 * `isLessonRequiredForRole`.
 *
 * Roles with no curriculum entry (portal roles, unknown codes) fall back to
 * showing all lessons — safe for development and for roles not yet configured.
 *
 * moduleId is required to disambiguate sub-module IDs that appear in more than
 * one module (e.g. a generic "lesson-4" id used in both soh-adjustment and
 * stocktake-essentials per the note at the top of this file).
 */
export function filterForRole(
  lessons: readonly CatalogueLesson[],
  roleId: string | null,
  moduleId: string,
  registry: ModuleRegistry = MODULE_REGISTRY,
): readonly CatalogueLesson[] {
  const keys = curriculumKeySet(roleId, false)
  const entry = registry[moduleId]
  return lessons.filter((l) => {
    if (keys !== null && !keys.has(`${moduleId}::${l.subModuleId}`)) return false
    // Tours are orientation content, not counted lessons.
    if (entry?.subModules[l.subModuleId]?.type === 'tour') return false
    return true
  })
}

/**
 * Does this role's curriculum mark this lesson `required: true`?
 *
 * The narrow question `filterForRole` used to answer for every number on the
 * screen, now asked only where it belongs — the Required Training list. Keeping
 * the two separate is what lets a lesson be visible and countable without being
 * demanded, which is exactly what `required: false` says.
 *
 * Roles with no curriculum entry are not gated: the registry's own
 * `SubModuleMeta.required` is then the only claim available, and it stands.
 */
export function isLessonRequiredForRole(
  roleId: string | null,
  moduleId: string,
  subModuleId: string,
): boolean {
  const keys = curriculumKeySet(roleId, true)
  if (keys === null) return true
  return keys.has(`${moduleId}::${subModuleId}`)
}

/**
 * Is this lesson in this role's curriculum at all — required OR optional?
 *
 * The same question `filterForRole` asks, for screens that build their lesson
 * list from the module JSON rather than from the catalogue —
 * `ModuleLessonsScreen`. Without it, a lesson could be absent from a role's
 * curriculum and still be opened from its module page, which is how Stocktake
 * Lesson 1 stayed reachable for RA_SK after it moved to BM/TL.
 *
 * Deliberately NOT `required`-only: optional lessons are offered, not hidden.
 * See the note on `curriculumKeySet`.
 *
 * Roles with no curriculum entry (portal roles, unknown codes, no identity) are
 * not gated and see every lesson.
 */
export function isLessonInCurriculum(
  roleId: string | null,
  moduleId: string,
  subModuleId: string,
): boolean {
  const keys = curriculumKeySet(roleId, false)
  if (keys === null) return true
  return keys.has(`${moduleId}::${subModuleId}`)
}

/**
 * Where the learner is in one lesson.
 *
 * Completion is `isLessonComplete`'s call and only its call. `started` is
 * anything short of that with a stage stamped on it — which is the guided-only
 * learner, and the state the old screen could not express.
 */
export function lessonStateOf(record: StageRecord | undefined): LessonState {
  if (isLessonComplete(record)) return 'complete'
  if (record?.guided !== undefined || record?.unguided !== undefined) return 'started'
  return 'not-started'
}

/** Stages this lesson takes to finish, per the content as it stands now. */
function stageCountOf(lesson: CatalogueLesson): number {
  return lesson.hasUnguidedStage ? STAGES_WITH_UNGUIDED : STAGES_GUIDED_ONLY
}

/**
 * Stages the learner has finished, capped at the lesson's stage count.
 *
 * A complete lesson reports every stage even when the content has since grown
 * one, so a module that is `4 of 4` can never render at less than 100%: the
 * store's stored `requiresUnguided` is what decided completion, and this must
 * not second-guess it with today's content.
 */
function stagesDoneOf(record: StageRecord | undefined, lesson: CatalogueLesson): number {
  const total = stageCountOf(lesson)
  if (isLessonComplete(record)) return total
  const done = (record?.guided === undefined ? 0 : 1) + (record?.unguided === undefined ? 0 : 1)
  return Math.min(done, total)
}

/**
 * Stage-weighted, NOT lesson-weighted, and that is the fix the learner asked
 * for: finishing the guided half of a two-stage lesson moves this off zero
 * instead of leaving the screen insisting nothing happened. Where no lesson is
 * half-finished it is identical to the lesson percentage.
 *
 * Exported so ModuleLessonsScreen renders the same percentage the dashboard
 * does. Two screens computing "how far in am I" from two formulas is how they
 * drift, and the drift shows up as the dashboard and the module page
 * disagreeing about the same lesson.
 */
export function percentOf(done: number, total: number): number {
  return total > 0 ? Math.round((done / total) * PERCENT) : 0
}

export interface Tally {
  readonly complete: number
  readonly started: number
  readonly stagesDone: number
  readonly stagesTotal: number
}

/**
 * Count one list of lessons. Exported for ModuleLessonsScreen, which tallies a
 * single module's lessons rather than the whole catalogue — same rules, same
 * `stagesDoneOf` treatment of a lesson whose content has since grown a stage.
 */
export function tallyOf(
  lessons: readonly CatalogueLesson[],
  stages: Readonly<Record<string, StageRecord>>,
): Tally {
  let complete = 0
  let started = 0
  let stagesDone = 0
  let stagesTotal = 0

  for (const lesson of lessons) {
    const record = stages[lesson.subModuleId]
    const state = lessonStateOf(record)
    if (state === 'complete') complete += 1
    if (state === 'started') started += 1
    stagesDone += stagesDoneOf(record, lesson)
    stagesTotal += stageCountOf(lesson)
  }

  return { complete, started, stagesDone, stagesTotal }
}

/**
 * Which pill the card lands in.
 *
 * `total === 0` is guarded explicitly — otherwise an empty module would satisfy
 * `complete === total` and announce itself Done.
 */
function statusOf(tally: Tally, total: number): ModuleStatus {
  if (total > 0 && tally.complete === total) return 'done'
  if (tally.complete > 0 || tally.started > 0) return 'in-progress'
  return 'pending'
}

/**
 * The card title.
 *
 * Taken from the registry's `moduleTag` tail (`"Module 01 • Ordering"` →
 * `"Ordering"`) rather than the JSON title, because the card is 142px wide with
 * `line-clamp-1` and was designed around the short form — `"Ordering
 * Essentials"` clips. Falls back to the authored title when there is no
 * registry entry or the tag carries no name.
 */
function titleOf(entry: ModuleRegistryEntry | undefined, authored: string): string {
  if (entry === undefined) return authored
  const name = entry.moduleTag.replace(MODULE_TAG_PREFIX, '').trim()
  return name === '' ? authored : name
}

/**
 * The line under the title. The registry's `subtitle` is a human descriptor
 * ("Automated Replenish") and there is no honest way to invent one for an
 * unregistered module, so the fallback states a fact instead: how many lessons
 * are in it.
 */
function subtitleOf(entry: ModuleRegistryEntry | undefined, total: number): string {
  if (entry !== undefined) return entry.subtitle
  return total === 1 ? '1 lesson' : `${total} lessons`
}

/**
 * The registry declares no priority field, so the due date stands in for it: a
 * lesson someone put a deadline on is the one to do first. Stated rather than
 * inferred would be better — add `priority` to `SubModuleMeta` when the client
 * agrees the vocabulary.
 */
function priorityOf(meta: SubModuleMeta): 'high' | 'medium' {
  return meta.dueLabel === null ? 'medium' : 'high'
}

function requiredLessonOf(
  lesson: CatalogueLesson,
  position: number,
  mod: CatalogueModule,
  entry: ModuleRegistryEntry,
  meta: SubModuleMeta | undefined,
  record: StageRecord | undefined,
): RequiredLessonModel {
  return {
    id: lesson.subModuleId,
    title: lesson.title.replace(LESSON_TITLE_PREFIX, ''),
    module: mod.title,
    submodule: `Lesson ${position}`,
    estimatedMinutes: Math.max(1, Math.ceil(lesson.stepCount / 6)),
    priority: meta ? priorityOf(meta) : 'medium',
    dueLabel: meta?.dueLabel ?? null,
    tags: meta?.tags ?? [],
    icon: entry.icon,
    accentColour: entry.accentColour,
    state: lessonStateOf(record),
    comingSoon: entry.comingSoon ?? false,
  }
}

/**
 * Registry order first, then whatever the catalogue had left.
 *
 * The registry's declaration order is the order the dashboard cards have always
 * been in (Ordering, Receiving, Stocktake, Returns, SOH, Expiry, Tasks), so
 * this preserves the existing screen exactly while putting modules nobody has
 * registered at the end rather than alphabetically first. `Array.sort` is
 * stable, so those keep catalogue order among themselves.
 */
function orderedByRegistry(
  catalogue: ContentCatalogue,
  registry: ModuleRegistry,
): CatalogueModule[] {
  const order = Object.keys(registry)
  const rankOf = (moduleId: string) => {
    const at = order.indexOf(moduleId)
    return at === -1 ? order.length : at
  }
  return [...catalogue].sort((a, b) => rankOf(a.moduleId) - rankOf(b.moduleId))
}

/**
 * The whole screen, from content + progress.
 *
 * Required lessons keep content order within registry-ordered modules — a
 * finished one stays where it is, carrying a `complete` state, rather than
 * vanishing or jumping. The learner should be able to see that what they
 * finished is what they finished.
 *
 * Module cards and overall progress count every lesson in the role's
 * curriculum; the Required Training list is the narrower cut. A role can
 * therefore be offered fourteen lessons and asked for two without either
 * number contradicting the other.
 */
export function deriveDashboard(
  catalogue: ContentCatalogue,
  stages: Readonly<Record<string, StageRecord>>,
  options: DeriveOptions = {},
): DashboardModel {
  const registry = options.registry ?? MODULE_REGISTRY
  const roleId = options.roleId ?? null

  // -- Pass 1: compute per-module tallies, including unregistered modules ------
  // Modules without a registry entry still render: their card falls back to
  // the authored title, a lesson-count subtitle, and empty icon/colour strings
  // that the screen resolves to its own fallbacks. They are never guessed into
  // the Required Training list (no metadata claim).
  interface ModuleData {
    mod: CatalogueModule
    entry: ModuleRegistryEntry | undefined
    lessons: readonly CatalogueLesson[]
    tally: Tally
    modulePercent: number
  }
  const moduleDataArr: ModuleData[] = []

  for (const mod of orderedByRegistry(catalogue, registry)) {
    const entry = registry[mod.moduleId]
    const lessons = filterForRole(mod.lessons, roleId, mod.moduleId, registry)
    const tally = tallyOf(lessons, stages)
    moduleDataArr.push({
      mod,
      entry,
      lessons,
      tally,
      modulePercent: percentOf(tally.complete, lessons.length),
    })
  }

  // -- Pass 2: build module cards -------------------------------------------
  const modules: ModuleCardModel[] = []
  let completeLessons = 0
  let totalLessons = 0
  let _stagesDone = 0
  let _stagesTotal = 0

  for (const { mod, entry, lessons, tally } of moduleDataArr) {
    // A module whose every lesson the role's curriculum filtered away is one
    // the role is not assigned. It used to render as a greyed-out `0/0` card;
    // the role matrix calls that "not assigned", so it is dropped entirely.
    // Skipping here rather than at the card keeps the banner's "N of M
    // Modules" and the overall percentages counting the same modules the
    // learner can actually open.
    //
    // Gated on the role having removed something, not on the count being zero:
    // a module that is empty in the catalogue has no role opinion attached and
    // still shows as `0 lessons`, which is how an unbuilt module reads.
    if (lessons.length === 0 && mod.lessons.length > 0) continue
    modules.push({
      id: entry?.navId ?? mod.moduleId,
      title: titleOf(entry, mod.title),
      subtitle: subtitleOf(entry, lessons.length),
      icon: entry?.icon ?? '',
      accentColour: entry?.accentColour ?? '',
      progress: tally.complete,
      total: lessons.length,
      started: tally.started,
      percent: percentOf(tally.complete, lessons.length),
      status: statusOf(tally, lessons.length),
      comingSoon: entry?.comingSoon ?? false,
    })

    completeLessons += tally.complete
    totalLessons += lessons.length
    _stagesDone += tally.stagesDone
    _stagesTotal += tally.stagesTotal
  }

  // -- Pass 3: collect all incomplete lessons, sorted by module proximity -------
  // "Module completion proximity" = modules closest to 100% appear first.
  // Trainees are encouraged to finish what's nearly done before starting something new.
  interface SortableRequired {
    model: RequiredLessonModel
    modulePercent: number
  }
  const sortable: SortableRequired[] = []

  for (const { mod, entry, lessons, modulePercent } of moduleDataArr) {
    if (!entry) continue
    lessons.forEach((lesson, index) => {
      const meta = entry.subModules[lesson.subModuleId]
      const record = stages[lesson.subModuleId]
      if (isLessonComplete(record)) return
      sortable.push({
        model: requiredLessonOf(lesson, index + 1, mod, entry, meta, record),
        modulePercent,
      })
    })
  }

  sortable.sort((a, b) => b.modulePercent - a.modulePercent)
  const requiredLessons = sortable.map((s) => s.model)

  return {
    modules,
    requiredLessons,
    overall: {
      completeLessons,
      totalLessons,
      percent: percentOf(completeLessons, totalLessons),
    },
  }
}
